import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, BookOpen, BookMarked, Book, Bookmark, BookmarkCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import { stripBismillah } from "@/lib/quranText";
import { SURAH_LIST, SURAH_MEANINGS } from "@/data/quranMeta";

const TOTAL_PAGES = 604;
// Surahs that get an ornamental header but NO Bismillah line:
//   1 (Al-Fatiha — bismillah is verse 1) and 9 (At-Tawba — no bismillah).
const NO_BISMILLAH_SURAHS = new Set([1, 9]);
const ARABIC_DIGITS = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

function toArabicNumeral(n) {
  return String(n).split("").map((c) => ARABIC_DIGITS[parseInt(c, 10)] ?? c).join("");
}

/**
 * MushafPage — renders a single 15-line-style Mushaf page from al-quran.cloud
 * Long-press → onAyahLongPress (opens action menu). Short tap → onAyahTap
 * (range-completion only). Double-tap → onAyahBookmark.
 */
export function MushafPage({
  pageNumber,
  highlightedAyah,
  selectedKeys,
  onAyahTap,
  onAyahLongPress,
  onAyahBookmark,
  onAyahsLoaded,
  lineHeightScale = 1,
  fontScale = 1,
  testIdSuffix = "",
}) {
  const [ayahs, setAyahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const pressOriginRef = useRef(null);

  const handlePressStart = (a, e) => {
    longPressFiredRef.current = false;
    const x = e?.clientX ?? e?.touches?.[0]?.clientX ?? null;
    const y = e?.clientY ?? e?.touches?.[0]?.clientY ?? null;
    pressOriginRef.current = x != null && y != null ? { x, y } : null;
    pressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      onAyahLongPress?.(a.surah_number, a.number_in_surah, x, y);
    }, 500);
  };

  // Cancel the long-press if the user moves > ~10px (i.e., is scrolling).
  const handlePressMove = (e) => {
    if (!pressTimerRef.current || !pressOriginRef.current) return;
    const cx = e?.clientX ?? e?.touches?.[0]?.clientX ?? null;
    const cy = e?.clientY ?? e?.touches?.[0]?.clientY ?? null;
    if (cx == null || cy == null) return;
    const dx = cx - pressOriginRef.current.x;
    const dy = cy - pressOriginRef.current.y;
    if (dx * dx + dy * dy > 100) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };
  // Double-tap detector: short tap = highlight/select (debounced 250ms),
  // double tap on same ayah = bookmark.
  const tapTimerRef = useRef(null);
  const tapKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API}/quran/page/${pageNumber}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        if (!cancelled) {
          const loaded = data.ayahs || [];
          setAyahs(loaded);
          onAyahsLoaded?.(pageNumber, loaded);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Could not load page");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  const handlePressEnd = (a) => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    if (longPressFiredRef.current) return;
    // Short tap — fires the parent-supplied tap callback (used for
    // range-completion only; tap-to-play and tap-to-bookmark have both been
    // removed per user spec).
    onAyahTap?.(a.surah_number, a.number_in_surah);
  };

  const handlePressCancel = () => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
  };

  // Group consecutive ayahs by surah so we can insert headers/bismillah dividers.
  const groups = [];
  for (const a of ayahs) {
    const last = groups[groups.length - 1];
    if (last && last.surah === a.surah_number) {
      last.ayahs.push(a);
    } else {
      groups.push({ surah: a.surah_number, surah_name: a.surah_english_name, surah_arabic: a.surah_name, ayahs: [a] });
    }
  }

  const headerInfo = ayahs[0];
  const juz = headerInfo?.juz;
  const pageStartsWithNewSurah = headerInfo?.number_in_surah === 1;

  return (
    <div
      data-testid={`mushaf-page${testIdSuffix}`}
      className="mushaf-parchment rounded-xl shadow-2xl border border-[#d8c98a] flex flex-col w-full overflow-hidden"
      style={{
        backgroundImage:
          "radial-gradient(circle at 20% 0%, rgba(201,162,39,0.08), transparent 60%), radial-gradient(circle at 80% 100%, rgba(201,162,39,0.08), transparent 60%)",
        minHeight: "70vh",
      }}
    >
      {/* Decorative top frame — gold double-hairline + centered crescent+star
          ornament, evoking the head of a Madinah Mushaf page. */}
      <MushafPageTopFrame onDark={pageStartsWithNewSurah} />

      {/* Top header strip — hidden when the page begins with a new Surah, since
          the dramatic Surah banner takes its place. */}
      {!pageStartsWithNewSurah && (
        <div className="flex items-center justify-between px-4 sm:px-6 pt-3 pb-2 border-b border-[#d8c98a]/60 text-[#7a5c1d] font-cinzel text-xs sm:text-sm">
          <span>{juz ? `Juz ${juz}` : ""}</span>
          <span className="font-amiri text-base sm:text-lg">
            {headerInfo?.surah_arabic || ""}
          </span>
          <span>صفحة {toArabicNumeral(pageNumber)}</span>
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="spinner w-10 h-10 border-[#7a5c1d] border-t-transparent" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center text-red-700 font-outfit p-6">
          {error}
        </div>
      ) : (
        <div
          className={`flex-1 px-4 sm:px-8 text-justify font-noto-naskh ${
            pageStartsWithNewSurah ? "pt-0 pb-6" : "py-6"
          }`}
          style={{
            direction: "rtl",
            fontSize: `clamp(${18 * fontScale}px, ${2.4 * fontScale}vw, ${26 * fontScale}px)`,
            lineHeight: String(2.4 * lineHeightScale),
          }}
        >
          {groups.map((g, gi) => {
            const isFirstAyahOfSurah = g.ayahs[0]?.number_in_surah === 1;
            const meta = SURAH_LIST.find((s) => s.number === g.surah);
            return (
              <div key={`${g.surah}-${gi}`}>
                {isFirstAyahOfSurah && (
                  <SurahDivider
                    surahNumber={g.surah}
                    arabicName={g.surah_arabic}
                    englishName={meta?.englishName || g.surah_name}
                    englishMeaning={SURAH_MEANINGS[g.surah]}
                    showBismillah={!NO_BISMILLAH_SURAHS.has(g.surah)}
                  />
                )}
                <p className={isFirstAyahOfSurah ? "pt-4" : ""}>
                  {g.ayahs.map((a) => {
                    const isHighlight =
                      highlightedAyah?.surah === a.surah_number &&
                      highlightedAyah?.ayah === a.number_in_surah;
                    const isSelected =
                      selectedKeys?.has(`${a.surah_number}:${a.number_in_surah}`) ?? false;
                    return (
                      <span
                        key={a.number}
                        data-testid={`page-ayah-${a.surah_number}-${a.number_in_surah}`}
                        onMouseDown={(e) => handlePressStart(a, e)}
                        onMouseUp={() => handlePressEnd(a)}
                        onMouseLeave={handlePressCancel}
                        onMouseMove={handlePressMove}
                        onTouchStart={(e) => handlePressStart(a, e)}
                        onTouchEnd={() => handlePressEnd(a)}
                        onTouchCancel={handlePressCancel}
                        onTouchMove={handlePressMove}
                        onContextMenu={(e) => e.preventDefault()}
                        title="Long-press to open actions"
                        className={`cursor-pointer rounded-sm transition-colors ${
                          isHighlight
                            ? "bg-[#C9A227]/40"
                            : isSelected
                            ? "bg-[#1B4D3E]/20 outline outline-1 outline-[#1B4D3E]/40"
                            : "hover:bg-[#C9A227]/15"
                        }`}
                      >
                        {/* Strip Bismillah from first ayah of surahs that already had a divider */}
                        {a.number_in_surah === 1 &&
                        !NO_BISMILLAH_SURAHS.has(a.surah_number)
                          ? stripBismillah(a.text)
                          : a.text}
                        {/* Ayah number medallion — gold-ringed Mushaf-style ornament */}
                        <span
                          className="inline-flex items-center justify-center align-middle mx-1 select-none relative"
                          style={{
                            width: "1.6em",
                            height: "1.6em",
                            fontSize: "0.55em",
                            background:
                              "radial-gradient(circle, #FBF6E9 0%, #F5E9C3 70%, #E8D5A3 100%)",
                            border: "1.5px solid #C9A227",
                            borderRadius: "50%",
                            color: "#1B4D3E",
                            fontWeight: 700,
                            fontFamily: "'Amiri', serif",
                            boxShadow:
                              "inset 0 0 0 1px #C9A227, inset 0 1px 2px rgba(255,255,255,0.6), 0 1px 2px rgba(0,0,0,0.08)",
                          }}
                        >
                          {toArabicNumeral(a.number_in_surah)}
                        </span>{" "}
                      </span>
                    );
                  })}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-[#7a5c1d] font-cinzel text-xs py-2 border-t border-[#d8c98a]/60">
        — {pageNumber} —
      </div>
    </div>
  );
}

function MushafPageTopFrame({ onDark = false }) {
  // Color tones flip slightly so the ornament reads on either parchment or
  // the dark-green Surah banner area beneath.
  const gold = onDark ? "#E8C75B" : "#9C7A1F";
  const lineFromTransparent = onDark
    ? "from-transparent to-[#E8C75B]/70"
    : "from-transparent to-[#9C7A1F]/70";
  const lineToTransparent = onDark
    ? "from-[#E8C75B]/70 to-transparent"
    : "from-[#9C7A1F]/70 to-transparent";

  return (
    <div
      aria-hidden="true"
      className={`relative flex items-center justify-center select-none ${
        onDark ? "bg-[#0D2818]" : "bg-[#FBF6E9]"
      }`}
      style={{ height: 30 }}
    >
      {/* Two thin gold lines stacked at top + bottom of the frame */}
      <div
        className="absolute left-3 right-3 top-[6px]"
        style={{
          borderTop: `1px solid ${gold}55`,
          borderBottom: `1px solid ${gold}55`,
          height: 3,
        }}
      />
      <div
        className="absolute left-6 right-6 bottom-[6px]"
        style={{
          borderTop: `1px solid ${gold}33`,
          borderBottom: `1px solid ${gold}33`,
          height: 2,
        }}
      />

      {/* Centered ornament: short flanking gradient lines + crescent + star */}
      <div className="relative flex items-center gap-2 px-3 z-10" style={{ background: onDark ? "#0D2818" : "#FBF6E9" }}>
        <div className={`hidden sm:block w-10 h-[1px] bg-gradient-to-r ${lineFromTransparent}`} />
        <svg
          width="28"
          height="14"
          viewBox="0 0 28 14"
          fill="none"
          className="block"
          style={{ color: gold }}
        >
          {/* Crescent moon */}
          <path
            d="M9.5 7 a4 4 0 1 0 0 0.4 a3 3 0 1 1 0 -0.4z"
            fill="currentColor"
            opacity="0.85"
          />
          {/* 5-pointed star */}
          <path
            d="M19 2.5 l1.1 2.4 l2.6 0.3 l-1.95 1.85 l0.55 2.6 l-2.3 -1.3 l-2.3 1.3 l0.55 -2.6 l-1.95 -1.85 l2.6 -0.3 z"
            fill="currentColor"
            opacity="0.85"
          />
        </svg>
        <div className={`hidden sm:block w-10 h-[1px] bg-gradient-to-l ${lineToTransparent}`} />
      </div>
    </div>
  );
}

function SurahDivider({
  surahNumber,
  arabicName,
  englishName,
  englishMeaning,
  showBismillah,
}) {
  return (
    <div
      className="select-none -mx-4 sm:-mx-8 my-2"
      data-testid={`surah-divider-${surahNumber}`}
      style={{ direction: "ltr" }}
    >
      {/* Dark green banner with ornate gold-bordered plaque (matches Surah Mode header) */}
      <div className="bg-gradient-to-b from-[#1B4D3E] to-[#0D2818] py-3 sm:py-4 px-4 relative overflow-hidden">
        {/* subtle dot/grain overlay */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #C9A227 0, transparent 40%), radial-gradient(circle at 80% 70%, #C9A227 0, transparent 35%)",
          }}
        />
        <div className="relative flex items-center justify-center">
          <div className="relative">
            {/* flanking gold lines */}
            <div className="absolute -left-10 sm:-left-16 top-1/2 -translate-y-1/2 w-8 sm:w-14 h-[1.5px] bg-gradient-to-r from-transparent to-[#C9A227]" />
            <div className="absolute -right-10 sm:-right-16 top-1/2 -translate-y-1/2 w-8 sm:w-14 h-[1.5px] bg-gradient-to-l from-transparent to-[#C9A227]" />
            <div className="border border-[#C9A227]/60 rounded-lg px-5 py-1 sm:px-7 sm:py-2 bg-[#0D2818]/40 shadow-[0_0_20px_rgba(201,162,39,0.08)]">
              <p
                className="font-amiri text-base sm:text-2xl text-[#E8D5A3] text-center leading-none"
                style={{ direction: "rtl" }}
              >
                {arabicName || `سورة ${toArabicNumeral(surahNumber)}`}
              </p>
            </div>
          </div>
        </div>
        {(englishName || englishMeaning) && (
          <p className="relative text-center text-[#8B9D83] font-outfit text-[10px] sm:text-xs mt-2 tracking-wide">
            {englishName}
            {englishMeaning ? ` • ${englishMeaning}` : ""}
          </p>
        )}
      </div>

      {/* Bismillah on cream parchment (separate band beneath the banner) */}
      {showBismillah && (
        <div className="py-2 sm:py-3 text-center mushaf-parchment border-b border-[#d8c98a]/40">
          <p
            className="font-amiri text-lg sm:text-2xl mushaf-bismillah"
            style={{ direction: "rtl" }}
          >
            بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * MushafPageView — full Mushaf experience:
 *  - Single page on mobile, two-page spread on lg+
 *  - Prev/Next + jump-to-page input
 *  - Long-press → onAyahLongPress (opens action menu)
 *  - Short tap   → onAyahTap (range-completion only)
 *  - Double-tap  → onAyahBookmark
 */
export default function MushafPageView({
  initialPage = 1,
  highlightedAyah,
  selectedKeys,
  onAyahTap,
  onAyahLongPress,
  onAyahBookmark,
  onPageAyahsLoaded,
  lineHeightScale = 1,
  fontScale = 1,
  controlsRef,
  pinnedPage,
  onPagePin,
}) {
  const [pageNum, setPageNum] = useState(
    Math.min(Math.max(parseInt(initialPage, 10) || 1, 1), TOTAL_PAGES)
  );
  const [jumpInput, setJumpInput] = useState("");
  // Decide single vs double page by simply comparing actual window dimensions.
  // Triggers double when:
  //   (a) viewport is at least 800px wide, AND
  //   (b) it's wider than tall (true landscape — covers phones in landscape too,
  //       but they fail (a)).
  // This sidesteps matchMedia quirks on Samsung/Android tablets where the
  // `orientation: landscape` feature isn't always reported on resize.
  // Mobile-portrait detection: short-side < 600px AND width <= height.
  // This is the "phone in portrait" class — on these devices we forbid
  // double-page rendering regardless of the manual toggle, because two
  // mushaf pages side-by-side become unreadably small. Tablets in portrait
  // are NOT considered mobile (their short side ≥ 600px) and may still use
  // double when the user manually toggles it.
  const computeMobilePortrait = () => {
    if (typeof window === "undefined") return false;
    return (
      Math.min(window.innerWidth, window.innerHeight) < 600 &&
      window.innerWidth <= window.innerHeight
    );
  };
  const computeWide = () => {
    if (typeof window === "undefined") return false;
    return window.innerWidth >= 800 && window.innerWidth > window.innerHeight;
  };
  const [isWide, setIsWide] = useState(computeWide);
  const [isMobilePortrait, setIsMobilePortrait] = useState(computeMobilePortrait);
  // Manual override for the double-page spread. 'auto' = follow viewport
  // detection (computeWide); 'on' = always double; 'off' = always single.
  // Persisted to localStorage so the user's choice survives navigation.
  const [spreadMode, setSpreadMode] = useState(() => {
    if (typeof window === "undefined") return "auto";
    const v = localStorage.getItem("quran_spread_mode");
    return v === "on" || v === "off" ? v : "auto";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran_spread_mode", spreadMode);
    }
  }, [spreadMode]);
  // Track per-page ayah lists so we can combine and bubble them up.
  const pageAyahsMapRef = useRef({});

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      setIsWide(computeWide());
      setIsMobilePortrait(computeMobilePortrait());
    };
    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    // Some Android browsers fire neither on rotation while in PWA mode,
    // so a defensive interval check covers the edge case.
    const id = setInterval(handler, 1500);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
      clearInterval(id);
    };
  }, []);

  // Effective wide flag: manual override wins, else auto-detection. We still
  // require a minimum 500px viewport width when forcing 'on' so two pages can
  // physically render without becoming unreadable. `isWide` already reacts to
  // resize/orientationchange, so we can derive a width-aware fallback by
  // OR-ing it with the bare condition.
  // HARD GUARD: phone-class devices in portrait can never render double — two
  // mushaf pages on a sub-600px short edge are illegible. The user must
  // rotate to landscape first.
  const effectiveWide = isMobilePortrait
    ? false
    : spreadMode === "on"
      ? isWide || (typeof window !== "undefined" && window.innerWidth >= 500)
      : spreadMode === "off"
        ? false
        : isWide;

  const step = effectiveWide ? 2 : 1;
  const onLastSpread = effectiveWide ? pageNum >= TOTAL_PAGES - 1 : pageNum >= TOTAL_PAGES;

  const goPrev = useCallback(() => {
    setPageNum((p) => Math.max(1, p - step));
  }, [step]);
  const goNext = useCallback(() => {
    setPageNum((p) => Math.min(TOTAL_PAGES, p + step));
  }, [step]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target?.tagName)) return;
      // RTL: ArrowLeft = next, ArrowRight = prev (visual flow)
      if (e.key === "ArrowLeft") goNext();
      if (e.key === "ArrowRight") goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev]);

  const handleJump = () => {
    const n = parseInt(jumpInput, 10);
    if (!isNaN(n) && n >= 1 && n <= TOTAL_PAGES) {
      setPageNum(n);
      setJumpInput("");
    }
  };

  // Long-press the page indicator → pin current page (Mushaf-style ribbon).
  // Tap (short press) does nothing — keeps the indicator readable.
  const pressTimerRef = useRef(null);
  const handlePinPress = () => {
    if (!onPagePin) return;
    pressTimerRef.current = setTimeout(() => {
      onPagePin(pageNum);
    }, 500);
  };
  const handlePinRelease = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };
  // On tablets (≥820px) AND in landscape: two-page spread. Portrait devices always get a single page.
  // CSS: `landscape:min-[820px]:flex-row-reverse` renders first child on right, second on left — but ONLY when viewport is ≥820px AND landscape-oriented.
  const showSecondPage = effectiveWide && pageNum < TOTAL_PAGES;

  const isPinnedHere = pinnedPage && (
    pinnedPage === pageNum || (showSecondPage && pinnedPage === pageNum + 1)
  );

  // Reset cached page ayahs when the spread changes (so stale pages don't leak).
  useEffect(() => {
    pageAyahsMapRef.current = {};
  }, [pageNum, showSecondPage]);

  // Bubble combined ayahs (page N + N+1 on lg) up to the parent in reading order.
  const reportCombinedAyahs = useCallback(() => {
    const m = pageAyahsMapRef.current;
    const first = m[pageNum];
    if (!first) return;
    if (showSecondPage) {
      const second = m[pageNum + 1];
      if (!second) return;
      onPageAyahsLoaded?.([...first, ...second]);
    } else {
      onPageAyahsLoaded?.([...first]);
    }
  }, [pageNum, showSecondPage, onPageAyahsLoaded]);

  const handlePageAyahsLoaded = useCallback(
    (page, ayahs) => {
      pageAyahsMapRef.current[page] = ayahs;
      reportCombinedAyahs();
    },
    [reportCombinedAyahs]
  );

  // Imperative handle so the parent (Reader.jsx) can flip pages from the
  // audio engine for seamless cross-page playback.
  useEffect(() => {
    if (!controlsRef) return;
    controlsRef.current = {
      goNext,
      goPrev,
      canGoNext: () => !onLastSpread,
      canGoPrev: () => pageNum > 1,
    };
  }, [controlsRef, goNext, goPrev, onLastSpread, pageNum]);

  return (
    <div data-testid="mushaf-page-view" className="space-y-4">
      {/* Pages */}
      <div
        className={`flex ${effectiveWide ? "flex-row-reverse" : "flex-col"} gap-4 justify-center items-stretch`}
      >
        <MushafPage
          pageNumber={pageNum}
          highlightedAyah={highlightedAyah}
          selectedKeys={selectedKeys}
          onAyahTap={onAyahTap}
          onAyahLongPress={onAyahLongPress}
          onAyahBookmark={onAyahBookmark}
          onAyahsLoaded={handlePageAyahsLoaded}
          lineHeightScale={lineHeightScale}
          fontScale={fontScale}
          testIdSuffix="-right"
        />
        {showSecondPage && (
          <MushafPage
            pageNumber={pageNum + 1}
            highlightedAyah={highlightedAyah}
            selectedKeys={selectedKeys}
            onAyahTap={onAyahTap}
            onAyahLongPress={onAyahLongPress}
            onAyahBookmark={onAyahBookmark}
            onAyahsLoaded={handlePageAyahsLoaded}
            lineHeightScale={lineHeightScale}
            fontScale={fontScale}
            testIdSuffix="-left"
          />
        )}
      </div>

      {/* Navigation Bar */}
      <div className="glass-card rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
        <Button
          data-testid="page-prev-btn"
          variant="ghost"
          onClick={goPrev}
          disabled={pageNum <= 1}
          className="rounded-full text-slate-200 hover:bg-white/5 disabled:opacity-30"
        >
          <ChevronRight className="w-4 h-4 mr-1" /> Previous
        </Button>

        <div className="flex items-center gap-2 flex-1 justify-center min-w-0">
          <BookOpen className="w-4 h-4 text-gold-400" />
          <span
            data-testid="page-indicator"
            onPointerDown={handlePinPress}
            onPointerUp={handlePinRelease}
            onPointerLeave={handlePinRelease}
            onPointerCancel={handlePinRelease}
            onContextMenu={(e) => e.preventDefault()}
            title="Long-press to pin this page as 'Resume here'"
            className="font-cinzel text-slate-200 text-sm cursor-pointer select-none"
          >
            {showSecondPage
              ? `Pages ${pageNum} — ${pageNum + 1}`
              : `Page ${pageNum}`}
            <span className="text-slate-500"> / {TOTAL_PAGES}</span>
          </span>
          {isPinnedHere ? (
            <BookmarkCheck
              data-testid="page-pin-indicator"
              className="w-4 h-4 text-gold-400"
              title="Pinned page"
            />
          ) : pinnedPage ? (
            <Bookmark
              data-testid="page-pin-indicator-other"
              className="w-3.5 h-3.5 text-slate-500"
              title={`Pinned at page ${pinnedPage}`}
            />
          ) : null}
          <Input
            data-testid="page-jump-input"
            value={jumpInput}
            onChange={(e) => setJumpInput(e.target.value.replace(/[^\d]/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && handleJump()}
            placeholder="Go to…"
            className="w-20 h-8 bg-islamic-blue-950/60 border-white/10 text-slate-200 text-center text-sm"
          />
          <Button
            data-testid="page-jump-btn"
            size="sm"
            onClick={handleJump}
            className="btn-gold h-8 px-3"
          >
            Go
          </Button>
          {!isMobilePortrait && (
            <Button
              data-testid="spread-toggle-btn"
              variant="ghost"
              size="sm"
              title={
                spreadMode === "auto"
                  ? `Auto · currently ${effectiveWide ? "double" : "single"} — tap to force single`
                  : spreadMode === "off"
                    ? "Single page — tap to force double"
                    : "Double page — tap for auto"
              }
              onClick={() =>
                setSpreadMode((m) =>
                  m === "auto" ? "off" : m === "off" ? "on" : "auto",
                )
              }
              className={`h-8 px-2 rounded-full text-slate-200 hover:bg-white/5 ${
                spreadMode !== "auto" ? "ring-1 ring-gold-400/40 text-gold-300" : ""
              }`}
            >
              {effectiveWide ? (
                <BookMarked className="w-4 h-4 mr-1" />
              ) : (
                <Book className="w-4 h-4 mr-1" />
              )}
              <span className="text-xs font-outfit">
                {spreadMode === "auto"
                  ? "Auto"
                  : spreadMode === "off"
                    ? "Single"
                    : "Double"}
              </span>
            </Button>
          )}
        </div>

        <Button
          data-testid="page-next-btn"
          variant="ghost"
          onClick={goNext}
          disabled={onLastSpread}
          className="rounded-full text-slate-200 hover:bg-white/5 disabled:opacity-30"
        >
          Next <ChevronLeft className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
